import { forwardRef, useEffect, useCallback, useState } from 'react';
import Lottie from "lottie-react";
import { useDispatch } from "react-redux";

import MainComponent from '../Main';
import ErrorPageComponent from '../ErrorPage';
import { useAppSelector } from '../../app/hooks';
import { CallBarState } from "../../constants/CallBarState";
import animationData from '../../assets/AnimationData.json';
import { useMetaDataLoad } from "../../Hooks/useMetaDataLoad";
import RealTimeClient from '../../Services/RealTimeClient';
import { getTwilioData } from '../../slices/twilio';
import { updateCallBarState } from "../../slices/callCentre";
import {
  getTeamWithOutGoingCallIdPreference,
  getUser,
  getUserTeam,
  getUserWrapupStatus,
  updateUserStatus,
} from '../../slices/auth';
import { getCallDispositions, getTenantSettings } from '../../slices/settings';
import { DefaultUserStatuses } from '../../constants/UserStatusesEnum';

import { SplashScreenProps } from './SplashScreen.types';
import { SplashContainerDiv, CenterDiv } from './SplashScreen.theme'; 

const SplashScreenComponent = forwardRef<HTMLDivElement, SplashScreenProps>(() => {
  const [getBeaconDispatched, setGetBeaconDispatched] = useState(false);
  const [userStatusSyncedAfterTenantSettings, setUserStatusSyncedAfterTenantSettings] = useState(false);

  const authData = useAppSelector((state:any) => state.auth);
  const tenantSettingsData = useAppSelector((state:any) => state.settings?.tenantSettings);
  const dispatch = useDispatch();

  const [errorCode, loading, fetched] = useMetaDataLoad();
  const tenantSettingsAvailable = Object.keys(tenantSettingsData).length > 0;

  const resolveInitialApiCalls = useCallback(() => {
    dispatch(getUserTeam(authData?.authUser?.userId));
    dispatch(getUser(authData?.authUser?.userId));
    dispatch(getTwilioData(authData?.authUser?.userId));
    dispatch(getUserWrapupStatus(authData?.authUser?.userId));
    dispatch(getTenantSettings());
    dispatch(getCallDispositions());
    dispatch(updateCallBarState(CallBarState.IDLE));
  }, [authData?.authUser?.userId, dispatch]);

  const resolveTeamWithOutGoingCallIdPreference = useCallback(() => {
    dispatch(getTeamWithOutGoingCallIdPreference());
  }, []);

  // Depend only on userId — the whole authUser object changes on every token refresh and would
  // redundantly re-dispatch getUserTeam, getUser, getTwilioData, getTenantSettings, etc.
  useEffect(() => {
    if (!!authData?.authUser?.userId) resolveInitialApiCalls();
  }, [authData?.authUser?.userId, resolveInitialApiCalls]);

  useEffect(() => {
    if(!!authData?.userTeam?.id) resolveTeamWithOutGoingCallIdPreference();
  }, [authData?.userTeam?.id, resolveTeamWithOutGoingCallIdPreference]);

  useEffect(() => {
    if (!tenantSettingsAvailable || !authData?.user?.id || userStatusSyncedAfterTenantSettings) {
      return;
    }

    const userStatuses = tenantSettingsData?.userStatuses;
    if (!Array.isArray(userStatuses) || userStatuses.length === 0) {
      return;
    }

    const availableStatus = userStatuses.filter((item: any) => item?.name === DefaultUserStatuses.Available);
    const availableId = availableStatus?.[0]?.id;
    const selectedStatusId = authData?.user?.userStatus?.id || availableId;
    const matched = userStatuses.find((item: any) => item?.id === selectedStatusId);
    if (!matched) {
      return;
    }

    dispatch(
      updateUserStatus({
        UserStatusId: matched.id,
        Available: matched.isAvailable,
        AwayFromDesk: matched.isAwayFromDesk,
      }),
    );
    setUserStatusSyncedAfterTenantSettings(true);
  }, [
    tenantSettingsAvailable,
    tenantSettingsData,
    authData?.user?.id,
    authData?.user?.userStatus?.id,
    userStatusSyncedAfterTenantSettings,
    dispatch,
  ]);

  useEffect(() => {
    // One tab owns the Beacon WebSocket. Followers wait until they take the phone line.
    if (tenantSettingsAvailable && !getBeaconDispatched) {
      RealTimeClient.armBeacon();
      setGetBeaconDispatched(true);
    }
  }, [
    tenantSettingsAvailable,
    getBeaconDispatched,
  ]);

    if (fetched) {
      return <MainComponent id='Main-Component' />
    } else if (errorCode !== 0) {
      return <ErrorPageComponent errorCode={errorCode}/>
    }

  return (
    <SplashContainerDiv>
      <Lottie
        loop={true}
        animationData={animationData}
        autoPlay={true}
        renderer='svg'
        style={{ height: '350px', width: '350px', alignSelf: 'center', placeContent: 'center', marginTop: 110 }}
      />
      <CenterDiv>Warming up the engines...</CenterDiv>
    </SplashContainerDiv>
  )
});

export default SplashScreenComponent;    